import type {
  CalculatedFeeRule,
  FeeItem,
} from '@/apis/extended/admin/interestRateApi';
import type { PublishedAprAssumptions } from '@/utils/advertisedApr';
import { AddFeedbackRequest, GetSignedUrlRequest } from '@/types/GlobalType';
import { rtkApi } from '../rtkApi';

/**
 * The active published rate config (GET /global/interest-rates/latest, public).
 * Only the fields public pages read are typed here; the admin API types the
 * full document.
 */
export interface InterestRateData {
  success: boolean;
  data: {
    version: number;
    programs: Record<
      string,
      {
        label: string;
        baseRate: number;
        aprAssumptions?: PublishedAprAssumptions;
        /** Admin fee adjustments and overrides of the calculators' fees. */
        feeItems?: FeeItem[];
        calculatedFeeRules?: CalculatedFeeRule[];
      }
    >;
    metadata?: { publishedAt: string | null };
    updatedAt?: string;
  };
}

/** Daily published base rates, oldest first (GET /global/interest-rates/history, public). */
export interface PublishedRateHistory {
  success: boolean;
  data: { date: string; rates: Record<string, number> }[];
}

export const globalApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getInterestRate: builder.query<number, void>({
      query: () => ({ url: `/global/interest-rate` }),
    }),
    getLatestInterestRates: builder.query<InterestRateData, void>({
      query: () => ({ url: `/global/interest-rates/latest` }),
      providesTags: ['InterestRates'],
    }),
    getPublishedRateHistory: builder.query<PublishedRateHistory, number>({
      query: (days) => ({
        url: `/global/interest-rates/history`,
        params: { days },
      }),
    }),
    getSignedUrl: builder.mutation<string, GetSignedUrlRequest>({
      query: (payload) => ({
        url: `/global/storage/signed-url`,
        params: payload,
      }),
    }),
    addFeedback: builder.mutation<void, AddFeedbackRequest>({
      query: (payload) => ({
        url: `/global/feedback`,
        method: 'POST',
        data: payload,
      }),
    }),
  }),
});

export const {
  useGetInterestRateQuery,
  useGetLatestInterestRatesQuery,
  useGetPublishedRateHistoryQuery,
  useGetSignedUrlMutation,
  useAddFeedbackMutation,
} = globalApi;
